"use client";

import { useEffect, useMemo, useState } from "react";
import { DEFAULT_CTEXTFX_CHARS_PER_SECOND } from "./Ctextfx.constants";
import {
  buildDecryptRevealed,
  maxDecryptRadius,
  pickDecryptOrigins,
} from "./Ctextfx.decrypt";

/** rAF-driven radius for multi-origin bidirectional Decrypt. */
export function useCtextfxDecryptRadius({
  maxRadius,
  active,
  play,
  playKey,
  charsPerSecond = DEFAULT_CTEXTFX_CHARS_PER_SECOND,
}: {
  maxRadius: number;
  active: boolean;
  play: boolean;
  playKey: string;
  charsPerSecond?: number;
}): number {
  const [radius, setRadius] = useState(active ? 0 : maxRadius);
  useEffect(() => {
    if (!active) {
      setRadius(maxRadius);
      return;
    }
    if (!play) {
      setRadius(0);
      return;
    }
    setRadius(0);
    let frame = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const nextRadius = Math.min(
        maxRadius,
        Math.floor(((now - start) / 1000) * charsPerSecond),
      );
      setRadius((previous) => (previous === nextRadius ? previous : nextRadius));
      if (nextRadius < maxRadius) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playKey, maxRadius, active, play, charsPerSecond]);
  return radius;
}

/** Shared decrypt mask for multi-segment surfaces (e.g. markdown paragraphs). */
export function useCtextfxDecryptReveal({
  length,
  playKey,
  active,
  play,
  charsPerSecond = DEFAULT_CTEXTFX_CHARS_PER_SECOND,
}: {
  length: number;
  playKey: string;
  active: boolean;
  play: boolean;
  charsPerSecond?: number;
}): {
  revealed: boolean[];
  settled: boolean;
} {
  const origins = useMemo(
    () => pickDecryptOrigins({ length, playKey }),
    [length, playKey],
  );
  const maxRadius = useMemo(
    () => maxDecryptRadius({ length, origins }),
    [length, origins],
  );
  const radius = useCtextfxDecryptRadius({
    maxRadius,
    active: active && length > 0,
    play,
    playKey,
    charsPerSecond,
  });
  const revealed = useMemo(
    () => buildDecryptRevealed({ length, origins, radius }),
    [length, origins, radius],
  );
  return {
    revealed,
    settled: !active || length === 0 || radius >= maxRadius,
  };
}
